import { Link, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { HomeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';

import DownloadIcon from 'assets/svg/DownloadIcon';
import { colorConstants } from 'config/colorConstants';
import { PPA_HOME_ROUTE } from 'config/urls';

const splitPackArchitectureTooltipLines = (text: string) =>
    text
        .replace(/\\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

const PackArchitectureInfoTooltipContent = ({ text }: { text: string }) => {
    const lines = splitPackArchitectureTooltipLines(text);

    if (lines.length <= 1) {
        return <span className="block text-sm leading-[22px]">{lines[0] ?? text}</span>;
    }

    const [intro, ...bullets] = lines;

    return (
        <div className="max-w-[320px] text-sm leading-[22px]">
            <p className="m-0 mb-2">{intro}</p>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2">
                        <span className="shrink-0 leading-[22px]" aria-hidden>
                            •
                        </span>
                        <span>{bullet}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

type PackArchitecturePageHeaderProps = {
    title: string;
    tooltip: string;
    onDownloadClick?: () => void;
    downloadDisabled?: boolean;
};

const PackArchitecturePageHeader = ({
    title,
    tooltip,
    onDownloadClick,
    downloadDisabled = false,
}: PackArchitecturePageHeaderProps) => {
    const navigate = useNavigate();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    useEffect(() => {
        const closeTooltipOnScroll = () => {
            setIsTooltipOpen(false);
        };

        window.addEventListener('scroll', closeTooltipOnScroll, true);

        return () => {
            window.removeEventListener('scroll', closeTooltipOnScroll, true);
        };
    }, []);

    return (
        <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                    aria-label="Go back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z"
                            fill={colorConstants.headerColor}
                        />
                    </svg>
                </button>
                <h1
                    className="m-0 text-[20px] font-semibold leading-8 tracking-[0.5px]"
                    style={{ color: colorConstants.blackTitle }}
                >
                    {title}
                </h1>
                {tooltip ? (
                    <Tooltip
                        open={isTooltipOpen}
                        onOpenChange={setIsTooltipOpen}
                        placement="right"
                        color={colorConstants.blackAlt1}
                        title={<PackArchitectureInfoTooltipContent text={tooltip} />}
                        overlayInnerStyle={{ padding: '8px 12px' }}
                    >
                        <InfoCircleOutlined
                            className="inline-flex cursor-pointer items-center justify-center text-[16px] leading-none"
                            style={{ color: colorConstants.greyAlt14 }}
                        />
                    </Tooltip>
                ) : null}
            </div>
            <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-borderGreyAl1 bg-transparent p-0 text-toolbarLabelGrey shadow-none hover:border-borderGreyAl1 hover:bg-transparent hover:text-toolbarLabelGrey disabled:cursor-not-allowed disabled:text-toolbarLabelGrey"
                aria-label="Download"
                disabled={downloadDisabled}
                onClick={onDownloadClick}
            >
                <DownloadIcon stroke="currentColor" width={14} height={14} />
            </button>
        </div>
    );
};

const PackArchitectureBreadcrumb = ({ title }: { title: string }) => (
    <nav className="flex items-center" aria-label="Breadcrumb">
        <Link to={PPA_HOME_ROUTE} className="flex items-center text-black/50 hover:text-black/65" aria-label="Home">
            <HomeOutlined className="text-[12px]" />
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-[12px] font-normal leading-[22px] text-black/50">Opportunity Assessment</span>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-[12px] font-medium leading-[22px] text-fontColor">{title}</span>
    </nav>
);

export { PackArchitectureBreadcrumb, PackArchitecturePageHeader };
